import {SearchSuggestionTypes} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/search/v1/service.types';
import {SearchNameType, SearchTypeTypes} from '@aboutyou/legacy-tracking-sdk';
import {
  createPath,
  useHistory,
  useInitialLocation,
  useSearchRoute,
} from '@aboutyou/router';
import {useSearchEventsUtils, useTrackSearch} from '@aboutyou/tracking';
import debounce from 'lodash.debounce';
import {useCallback} from 'react';
import {onClickSuggestionType} from './useSearchBar';
import {useStoredSearchTerms} from './useStoredSearchTerms';
import {useGetSearchRedirect} from '../../../Search/ConditionalSearchTermRedirect';

export type SearchSuggestionState = {wasSearchPerformed?: boolean};

export const SEARCH_BAR_ANIMATION_TIME_MS = 70;
const TRACK_SUGGEST_DEBOUNCE_DELAY_MS = 500;

export const useSearchSuggestions = ({
  isSearchBarExpanded,
  isTabletMode,
  collapseAndBlurInput,
  handleInputOnChange,
  inputRef,
  searchValue,
  handleRemoveInputFocus,
}: {
  isSearchBarExpanded: boolean;
  isTabletMode: boolean;
  collapseAndBlurInput: (shouldCollapse?: boolean) => void;
  handleInputOnChange: (term: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  searchValue: string;
  handleRemoveInputFocus: VoidFunction;
}) => {
  const {addSearchTerm} = useStoredSearchTerms({limitSearchHistory: true});
  const trackSearch = useTrackSearch();
  const searchRoute = useSearchRoute();
  const {updateSearchEventContextParams} = useSearchEventsUtils();
  const history = useHistory();
  const filterOutSearchParameters = useFilterOutSearchParameters();
  const getSearchRedirect = useGetSearchRedirect();

  const clearSearchAndFocus = () => {
    trackSearch({
      searchAction: 'click',
      searchName: 'searchClose',
    });

    if (isSearchBarExpanded) {
      collapseAndBlurInput();
    } else {
      handleInputOnChange('');
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
    }
  };

  const createSearchPath = (searchTerm: string) => {
    const searchQueryParams = new URLSearchParams();
    searchQueryParams.append('term', searchTerm);

    const searchPathWithTerm = createPath({
      pathname: searchRoute,
      search: searchQueryParams.toString(),
    });

    return searchPathWithTerm;
  };

  const onSearchInputEnter = async () => {
    if (searchValue !== '') {
      collapseAndBlurInput();
      addSearchTerm({term: searchValue});

      const searchRedirect = await getSearchRedirect?.(searchValue);

      updateSearchEventContextParams({
        searchName: 'searchHotkey',
        searchTerm: searchValue,
        // BE will take care of returning the correct searchType
        searchType: (searchRedirect?.searchType || '') as SearchTypeTypes,
      });

      if (searchRedirect?.url) {
        const [pathname, search] = searchRedirect.url.split('?');

        history.push({
          pathname,
          search,
          state: {wasSearchPerformed: true} as SearchSuggestionState,
        });
        return;
      }

      const searchQueryParams = new URLSearchParams();
      searchQueryParams.append('term', searchValue);
      history.push({
        pathname: searchRoute,
        search: searchQueryParams.toString(),
        state: {wasSearchPerformed: true} as SearchSuggestionState,
      });
    }
  };

  const onClickSuggestion: onClickSuggestionType = ({
    searchTerm,
    searchType,
    url,
  }) => {
    if (searchTerm === '') {
      return;
    }

    if (inputRef.current) {
      inputRef.current.value = searchTerm;
    }

    handleInputOnChange(searchTerm);

    const searchEventContextParams = searchValue
      ? {
          searchTerm: searchValue,
          searchName: searchType,
          searchTermCompleted: searchTerm,
        }
      : {
          searchTerm,
          searchName: searchType,
        };

    updateSearchEventContextParams(searchEventContextParams);

    const targetUrl = filterOutSearchParameters(url);
    addSearchTerm({term: searchTerm, url: targetUrl});
    handleRemoveInputFocus();
    history.push(targetUrl);
  };

  const searchDebounceConfig = {
    debounceTime: 100,
    options: {
      leading: true,
      maxWait: 150,
    },
  };

  const handleInputOnChangeWithDebounce = useCallback(
    debounce(
      (searchValue: string) => {
        handleInputOnChange(searchValue);
      },
      searchDebounceConfig.debounceTime,
      searchDebounceConfig.options,
    ),
    [handleInputOnChange],
  );

  const handleInputOnChangeTrackingWithDebounce = useCallback(
    debounce((searchValue: string) => {
      trackSearch({searchAction: 'search_suggest', searchTerm: searchValue});
    }, TRACK_SUGGEST_DEBOUNCE_DELAY_MS),
    [],
  );

  const shouldShowCloseIcon = Boolean(
    (isTabletMode && isSearchBarExpanded) ||
      (!isTabletMode && searchValue.length),
  );

  return {
    shouldShowCloseIcon,
    onClickSuggestion,
    onSearchInputEnter,
    clearSearchAndFocus,
    createSearchPath,
    handleInputOnChangeTrackingWithDebounce,
    handleInputOnChangeWithDebounce,
    addSearchTerm,
    searchDebounceConfig,
  };
};

export const getSearchSuggestionName = (type: SearchSuggestionTypes) => {
  const searchNamesByValueMap: Record<SearchSuggestionTypes, SearchNameType> = {
    0: 'unspecified',
    1: 'suggestedTerm',
    2: 'suggestedBrand',
  };

  return searchNamesByValueMap[type];
};

export const useFilterOutSearchParameters = () => {
  const {protocol, host} = useInitialLocation();

  const filterOutSearchParameters = (url: string) => {
    const redirectUrl = new URL(`${protocol}://${host}${url}`);
    redirectUrl.searchParams.delete('is_h');
    redirectUrl.searchParams.delete('is_s');

    if (url.startsWith('/search')) {
      redirectUrl.searchParams.delete('search_type');
    } else {
      redirectUrl.searchParams.delete('term');
    }

    return `${redirectUrl.pathname}${redirectUrl.search}`;
  };

  return filterOutSearchParameters;
};
